import {
  ANALYTICS_GLOBAL_CLICK_EVENT,
  ANALYTICS_NO_VALUE,
  type BaseImageMetrics,
  ImageClickID,
  type ImageClickIDType,
  ImageFunction,
  ImageType,
  trackEvent,
} from '@dx-ui/config-metrics';
import type { CarouselSingleMetrics } from '../types/carousel-analytics';

type GenerateCarouselSingleMetricsParams = {
  metrics?: Partial<BaseImageMetrics>;
  position?: number;
  images?: { alt: string }[];
  itemTitle?: string;
  clickID: ImageClickIDType;
  imageFunction?: (typeof ImageFunction)['Link'];
};

/**
 * Utility function to generate carousel metrics
 */
export function generateCarouselSingleMetrics({
  metrics,
  position,
  images,
  itemTitle = ANALYTICS_NO_VALUE,
  clickID = ImageClickID.Scroll,
  imageFunction,
}: GenerateCarouselSingleMetricsParams): CarouselSingleMetrics {
  const carouselMetrics = {
    click: {
      ...metrics?.click,
      clickID,
    },
    osc: {
      ...metrics?.osc,
      image: {
        ...metrics?.osc?.image,
        headline: metrics?.osc?.image?.headline || ANALYTICS_NO_VALUE,
        count: images?.length || 0,
        position: position || 0,
        itemTitle: itemTitle || ANALYTICS_NO_VALUE,
        type: ImageType.Carousel,
      },
    },
  };

  if (imageFunction === ImageFunction.Link) {
    carouselMetrics.osc.image = {
      ...carouselMetrics.osc.image,
      function: ImageFunction.Link,
    };
  }

  return carouselMetrics;
}

/**
 * Utility function to generate CPM carousel metrics
 * The returned object will serve as a partial image metric object to be extended within the CarouselSingle component
 */
export function generateCpmCarouselMetrics(
  headline: string | null | undefined,
  clickID?: ImageClickIDType
): Partial<BaseImageMetrics> {
  return {
    click: {
      clickID: clickID ?? ImageClickID.Img,
    },
    osc: {
      image: {
        // All the other CarouselSingleMetrics attributes will be set in CarouselSingle
        headline: headline || ANALYTICS_NO_VALUE,
        type: ImageType.Carousel,
      },
    },
  };
}

/** App-agnostic semantic payload for carousel arrow/swipe navigation. */
export type CarouselNavigationPayload = {
  clickId: ImageClickIDType;
  type: (typeof ImageType)['Carousel'];
  count: number;
  position: number;
  headline: string;
  itemTitle: string;
};

/** App-agnostic semantic payload for carousel caption/image click. */
export type CarouselImageClickPayload = CarouselNavigationPayload & {
  imageFunction: (typeof ImageFunction)['Link'];
};

type CarouselDispatchMetricsParams = Omit<
  GenerateCarouselSingleMetricsParams,
  'clickID' | 'imageFunction'
>;

type DispatchCarouselInteractionParams =
  | {
      interaction: 'navigation';
      metricsParams: CarouselDispatchMetricsParams;
      onInteraction?: (payload: CarouselNavigationPayload) => void;
      isLegacyCarouselAnalyticsEnabled?: boolean;
    }
  | {
      interaction: 'imageClick';
      metricsParams: CarouselDispatchMetricsParams;
      onInteraction?: (payload: CarouselImageClickPayload) => void;
      isLegacyCarouselAnalyticsEnabled?: boolean;
    };

/** Fields shared by both interactions. Navigation reports exactly these. */
function toCarouselPayloadBase(metrics: CarouselSingleMetrics): CarouselNavigationPayload {
  return {
    clickId: metrics.click.clickID,
    type: ImageType.Carousel,
    count: metrics.osc.image?.count ?? 0,
    position: metrics.osc.image?.position ?? 0,
    headline: metrics.osc.image?.headline || ANALYTICS_NO_VALUE,
    itemTitle: metrics.osc.image?.itemTitle || ANALYTICS_NO_VALUE,
  };
}

/**
 * Routes one carousel interaction: semantic callback OR legacy trackEvent, never both.
 */
export function dispatchCarouselInteraction({
  isLegacyCarouselAnalyticsEnabled = true,
  ...params
}: DispatchCarouselInteractionParams): void {
  const metrics =
    params.interaction === 'navigation'
      ? generateCarouselSingleMetrics({ ...params.metricsParams, clickID: ImageClickID.Scroll })
      : generateCarouselSingleMetrics({
          ...params.metricsParams,
          clickID: ImageClickID.Img,
          imageFunction: ImageFunction.Link,
        });

  if (isLegacyCarouselAnalyticsEnabled || !params.onInteraction) {
    trackEvent(ANALYTICS_GLOBAL_CLICK_EVENT, metrics);
    return;
  }

  if (params.interaction === 'navigation') {
    params.onInteraction(toCarouselPayloadBase(metrics));
    return;
  }

  params.onInteraction({
    ...toCarouselPayloadBase(metrics),
    imageFunction: ImageFunction.Link,
  });
}
